// frontend/src/components/athleteEditor/VerificationHistoryCard.jsx
// Verification-history table for the 1a screen — same `.ae-card` shell as
// MeasurablesCard, humanized Stat labels via statDisplayLabel(groups, key).
// Port of the page's old VerificationLogSection; drops the Action column per
// the approved prototype (docs/design/athlete-editor-redesign), which shows
// only When/Stat/Value/Verified by/Camp.
import React, { useState } from 'react';
import { statDisplayLabel } from '../../lib/athleteEditorModel';

// Server timestamps are UTC with no tz suffix — parse as UTC, render local,
// matching the prototype's "Jul 3 · 2:45 AM" mono format.
function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + 'Z');
  if (isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${date} · ${time}`;
}

const RECENT = 5;

/**
 * Verification history for the staff/partner read view (`.adm` look). Same
 * `logs` source and columns as VerificationHistoryCard. Shows the newest five
 * ("Last 5 of N") and a button to show the rest, so nothing becomes
 * unreachable.
 */
export function VerificationHistoryTable({ logs, loading, groups }) {
  const rows = logs || [];
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? rows : rows.slice(0, RECENT);
  const meta = loading ? 'Loading…'
    : rows.length === 0 ? 'No entries'
      : rows.length <= RECENT || showAll ? `${rows.length} ${rows.length === 1 ? 'entry' : 'entries'}`
        : `Last ${RECENT} of ${rows.length}`;
  return (
    <section className="adm__card adm__card--flush">
      <div className="adm-card__head">
        <h2 className="adm-card__title">Verification history</h2>
        <span className="adm-card__meta">{meta}</span>
      </div>
      {!loading && rows.length === 0 && <div className="adm-empty">No verification entries.</div>}
      {!loading && rows.length > 0 && (
        <div className="adm-tablewrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Stat</th>
                <th className="adm-th--right">Value</th>
                <th>By</th>
                <th>Where</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(log => (
                <tr key={log.id}>
                  <td className="adm-vh__when">{fmtWhen(log.created_at)}</td>
                  <td className="adm-vh__stat">{statDisplayLabel(groups, log.stat_name)}</td>
                  <td className="adm-td--num">{log.stat_value || '—'}</td>
                  <td>
                    {log.verified_by || '—'}
                    {log.verified_by_2 && <> + {log.verified_by_2}</>}
                    {log.verification_type === 'dual' && <span className="adm-vh__where"> (dual)</span>}
                  </td>
                  <td className="adm-vh__where">{log.camp_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && rows.length > RECENT && (
        <div className="adm-vh__more">
          <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => setShowAll(v => !v)}>
            {showAll ? `Show last ${RECENT}` : `Show all ${rows.length}`}
          </button>
        </div>
      )}
    </section>
  );
}

export default function VerificationHistoryCard({ logs, loading, groups }) {
  const rows = logs || [];
  // Collapsed by default — it's reference detail, not something you scan every visit.
  const [open, setOpen] = useState(false);
  return (
    <div className="ae-card">
      <button
        type="button"
        className="ae-card__head ae-card__head--toggle"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <h2 className="ae-card__title">Verification History</h2>
        <span className="ae-card__hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {rows.length} {rows.length === 1 ? 'ENTRY' : 'ENTRIES'}
          <span aria-hidden="true" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', display: 'inline-block' }}>▸</span>
        </span>
      </button>
      {open && loading && <div className="ae-history__loading">Loading…</div>}
      {open && !loading && rows.length === 0 && (
        <div className="ae-history__empty">No verification entries.</div>
      )}
      {open && !loading && rows.length > 0 && (
        <div className="ae-history">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Stat</th>
                <th className="ae-history__val">Value</th>
                <th>Verified by</th>
                <th>Camp</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(log => (
                <tr key={log.id}>
                  <td className="ae-history__when">{fmtWhen(log.created_at)}</td>
                  <td className="ae-history__stat">{statDisplayLabel(groups, log.stat_name)}</td>
                  <td className="ae-history__val">{log.stat_value || '—'}</td>
                  <td className="ae-history__by">
                    {log.verified_by || '—'}
                    {log.verified_by_2 && <> + {log.verified_by_2}</>}
                    {log.verification_type === 'dual' && <span className="ae-history__dual"> (dual)</span>}
                  </td>
                  <td className="ae-history__camp">{log.camp_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
